import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Form,
  FormFeedback,
  Button,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import TableContainer from "components/Common/TableContainer";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  getEntitlements,
  createEntitlement,
  updateEntitlement,
  deleteEntitlement,
} from "api";
import { handleError } from "utils/errorHandler";

const ManageEntitlements = () => {
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);

  const toggle = () => setModal(!modal);
  const checkIfEntitlementExists = (key) => {
    return entitlements.some((entitlement) => entitlement.entitlementKey === key);
  };
 
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      entitlementKey: selectedItem?.entitlementKey || "",
      entitlementName: selectedItem?.entitlementName || "",
    },
    validationSchema: Yup.object({
      entitlementKey: Yup.string().required("Key is required"),
      entitlementName: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          userRoleId: 2,
          userRole: "organizer",
          isPremium: true,
        };

        setLoading(true);

        if (isEdit) {
          await updateEntitlement(selectedItem.entitlementId, payload);
          const refreshed = await getEntitlements();
          setEntitlements(refreshed);
          toast.success("Updated successfully");
        } else {
          await createEntitlement(payload);
          const refreshed = await getEntitlements();
          setEntitlements(refreshed);
          toast.success("Created successfully");
        }

        validation.resetForm();
        setSelectedItem(null);
        setIsEdit(false);
        setKeyManuallyEdited(false);
        toggle();
      } catch(error) {
         console.log(error);
        handleError(error);
      } finally {
        setLoading(false);
      }
    },
  });


  // Auto-generate entitlementKey from entitlementName
  useEffect(() => {
    if (!isEdit && !keyManuallyEdited && validation.values.entitlementName) {
      const generatedKey = validation.values.entitlementName
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
      validation.setFieldValue("entitlementKey", generatedKey);
    }
  }, [validation.values.entitlementName, isEdit, keyManuallyEdited]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getEntitlements();
        setEntitlements(data);
      } catch {
        toast.error("Failed to load entitlements");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onClickDelete = (item) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteEntitlement(selectedItem.entitlementId);
      const refreshed = await getEntitlements();
      setEntitlements(refreshed);
      toast.success("Deleted");
    } catch(error) {
      handleError(error);
    }
    setDeleteModal(false);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEdit(true);
    setKeyManuallyEdited(true); // In edit mode, don't auto-generate
    validation.resetForm();
    toggle();
  };

  const handleAddNew = () => {
    setSelectedItem(null);
    setIsEdit(false);
    setKeyManuallyEdited(false); // Reset flag for new items
    validation.resetForm();
    toggle();
  };

  const columns = useMemo(
    () => [
      { header: "Key", accessorKey: "entitlementKey" },
      { header: "Name", accessorKey: "entitlementName" },
      {
        header: "Action",
        cell: (cellProps) => {
          const item = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem onClick={() => handleEdit(item)}>
                  Edit
                </DropdownItem>
                <DropdownItem onClick={() => onClickDelete(item)}>
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Admin" breadcrumbItem="Manage Entitlements" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={entitlements}
                      isGlobalFilter
                      isPagination
                      SearchPlaceholder="Search..."
                      isAddButton
                      handleUserClick={handleAddNew}
                      buttonClass="btn btn-success btn-rounded mb-2"
                      buttonName="Add Entitlement"
                      tableClass="align-middle table-hover w-100"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate pagination-rounded"
                      pagination="pagination"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Entitlement" : "Add Entitlement"}
            </ModalHeader>

            <ModalBody>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                }}
              >
                

                <div className="mb-3">
                  <Label>Entitlement Name</Label>
                  <Input
                    name="entitlementName"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.entitlementName}
                    invalid={
                      validation.touched.entitlementName &&
                      validation.errors.entitlementName
                    }
                  />
                  <FormFeedback>
                    {validation.errors.entitlementName}
                  </FormFeedback>
                </div>
                <div className="mb-3">
                  <Label>Entitlement Key</Label>
                  <Input
                    name="entitlementKey"
                    type="text"
                    onChange={(e) => {
                      setKeyManuallyEdited(true);
                      validation.handleChange(e);
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.entitlementKey}
                    invalid={
                      validation.touched.entitlementKey &&
                      validation.errors.entitlementKey
                    }
                  />
                  <FormFeedback>
                    {validation.errors.entitlementKey}
                  </FormFeedback>
                </div>

                <div className="text-end">
                  <Button color="success" type="submit">
                    Save
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </>
  );
};

export default withRouter(ManageEntitlements);
